import ExcelJS from 'exceljs';
import { sanitiseValue, styleHeaderRow } from './buildExcelWorkbook';
import { throwIfAborted } from '../utils/downloadBlob';
import type {
  FdaSourceExportResponse,
  FdaSourceKey,
} from '../services/api/domains/products/types';

/**
 * Rows written between yields to the event loop. A 100,000-row source built
 * in one synchronous loop freezes the page (no repaint, Cancel unclickable);
 * yielding every chunk keeps the UI responsive and lets an abort land.
 */
const ROWS_PER_CHUNK = 2000;

const yieldToEventLoop = (): Promise<void> =>
  new Promise(resolve => setTimeout(resolve, 0));

/** Worksheet names, in the order sheets are written (one sheet per source). */
const SHEET_NAMES: ReadonlyArray<readonly [FdaSourceKey, string]> = [
  ['ob', 'Orange Book'],
  ['ndc', 'NDC'],
  ['dmf', 'USDMF'],
];

/**
 * Sources whose export hit the server's row cap, as `[sheet name, exported,
 * total]` -- used for the in-workbook notes sheet and the UI notice.
 */
export function getTruncatedSources(
  response: FdaSourceExportResponse
): Array<{ name: string; exported: number; total: number }> {
  return SHEET_NAMES.flatMap(([key, name]) => {
    const result = response[key];
    return result?.truncated
      ? [{ name, exported: result.rows.length, total: result.total }]
      : [];
  });
}

/**
 * Builds the FDA Sources export workbook (docs/features/metrics-overview-
 * tab.md, OVW-014): one worksheet per requested source holding every column
 * the server returned (all product-table metadata), header row styled like
 * the Products export. A source that hit the server's row cap is flagged on
 * an extra "Export Notes" sheet.
 *
 * Cell values are plain strings/numbers (never formulas), so there is no
 * formula-injection risk.
 *
 * Rows are written in chunks that yield to the event loop; `signal` is
 * checked at each yield, so a Cancel click stops the build (throws an
 * `AbortError`) instead of waiting for the whole workbook.
 *
 * @returns the workbook serialised as an .xlsx Blob
 */
export async function buildFdaSourceWorkbook(
  response: FdaSourceExportResponse,
  signal?: AbortSignal
): Promise<Blob> {
  const workbook = new ExcelJS.Workbook();

  for (const [key, name] of SHEET_NAMES) {
    const result = response[key];
    if (!result) continue;

    const sheet = workbook.addWorksheet(name);
    sheet.columns = result.columns.map(column => ({
      header: column,
      key: column,
      width: 22,
    }));
    styleHeaderRow(sheet.getRow(1));

    for (let index = 0; index < result.rows.length; index += 1) {
      if (index > 0 && index % ROWS_PER_CHUNK === 0) {
        await yieldToEventLoop();
        if (signal) throwIfAborted(signal);
      }
      const row = result.rows[index];
      const rowData: Record<string, string | number> = {};
      for (const column of result.columns) {
        rowData[column] = sanitiseValue(row[column]);
      }
      sheet.addRow(rowData);
    }
    sheet.views = [{ state: 'frozen', xSplit: 0, ySplit: 1 }];
  }

  const truncated = getTruncatedSources(response);
  if (truncated.length > 0) {
    const notes = workbook.addWorksheet('Export Notes');
    notes.columns = [
      { header: 'Sheet', key: 'sheet', width: 20 },
      { header: 'Note', key: 'note', width: 90 },
    ];
    styleHeaderRow(notes.getRow(1));
    truncated.forEach(({ name, exported, total }) =>
      notes.addRow({
        sheet: name,
        note: `Truncated: ${exported.toLocaleString()} of ${total.toLocaleString()} matching rows exported (server row cap). Narrow the search or filters to export the rest.`,
      })
    );
  }

  const buffer = await workbook.xlsx.writeBuffer();
  return new Blob([buffer], {
    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  });
}
